import { useCallback, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import "@/compat/historyState";
import { useAgeGatedAction } from "@/hooks/useAgeFloor";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useRememberedDevices } from "@/hooks/useRememberedDevices";
import { useSavedRooms } from "@/hooks/useSavedRooms";
import { waitForAccountId } from "@/hooks/useAuthSession";
import { noteRoomCreated } from "@/hooks/useRoomEntry";
import type { EntryRefusal } from "@/engine/state/roomAccess";
import { createRoomForEntry } from "@/engine/state/roomCreation";
import { roomPath } from "@/engine/state/roomIdentity";
import { getSettings } from "@/engine/state/settings";

import { AgeQuestion } from "./entry/AgeQuestion";
import { EntryPage } from "./entry/EntryPage";

export interface EntryScreenProps {
  /** Why a room sent this visitor back, when one did. */
  refusal?: EntryRefusal | null;
  /** The code the Join door opens on — the room they were last in. */
  prefillCode?: string;
  /** The door home opens on, where a sign-in round trip names one. */
  opensOn?: "create" | undefined;
}

/**
 * The way in, and nothing else.
 *
 * Until #610 this screen and the room were one component handing over between
 * them, and each drew the other's states: a refused code and a walk in were
 * drawn on the door while the room worked. They are two addresses now, so this
 * one knows only what it can decide for itself — a name, a code, a press — and
 * the room's address draws everything that happens after the press.
 *
 * The one wait left here is the one that can refuse before there is any room to
 * go to: creating needs an account, and the row is written before the visitor
 * is sent to an address that names it.
 */
export function EntryScreen({
  refusal = null,
  prefillCode = "",
  opensOn,
}: EntryScreenProps) {
  const navigate = useNavigate();
  const { session } = useAuthSession();
  const {
    rooms: savedRooms,
    counts: roomCounts,
    forget: forgetRoom,
  } = useSavedRooms(session?.user.id ?? null, undefined);
  const [busy, setBusy] = useState(false);
  const [entryError, setEntryError] = useState<EntryRefusal | null>(refusal);

  // The press chooses the room; who the visitor is, and how they arrive, is
  // asked at its threshold.
  const onJoinRoom = useCallback(
    (code: string) => void navigate({ to: roomPath(code) }),
    [navigate],
  );

  const onCreateRoom = useCallback(
    async (name?: string) => {
      setEntryError(null);
      setBusy(true);
      // Frozen at the press, and carried to the room's address: the store may
      // hydrate, or change in another tab, while the row is being written, and
      // a room opens on what the visitor had in front of them when they pressed.
      const preferences = getSettings();
      const opened = await createRoomForEntry({
        roomMode: preferences.roomMode,
        displayName: name,
        lookupAccount: waitForAccountId,
        note: noteRoomCreated,
      });
      setBusy(false);
      if (opened.refusal) {
        setEntryError(opened.refusal);
        return;
      }
      void navigate({
        to: roomPath(opened.code),
        state: {
          opening: true,
          recordRefused: !opened.recorded,
          preferences,
          roomTitle: name?.trim() || undefined,
        },
      });
    },
    [navigate],
  );

  // Asked before the row is written, not at the new room's threshold: by then
  // the room would exist for somebody who is not allowed to open it.
  const age = useAgeGatedAction();

  const [micId, setMicId] = useState<string | null>(null);
  const [cameraId, setCameraId] = useState<string | null>(null);
  const { rememberAudioInput, rememberCamera } = useRememberedDevices({
    selectedAudioInput: micId,
    selectedCamera: cameraId,
    onAudioInputChange: setMicId,
    onCameraChange: setCameraId,
  });

  return (
    <>
      <EntryPage
        onCreateRoom={(name) => age.run(() => void onCreateRoom(name))}
        onJoinRoom={onJoinRoom}
        onHandedOver={(code) =>
          void navigate({ to: roomPath(code), state: { handedOver: true } })
        }
        onTryDemo={() => void navigate({ to: "/demo" })}
        savedRooms={savedRooms}
        roomCounts={roomCounts}
        onForgetRoom={forgetRoom}
        isConnecting={busy}
        peekEnabled={!busy}
        prefillCode={prefillCode}
        opensOn={opensOn}
        entryError={entryError}
        walkIn={busy ? "pressed" : null}
        selectedMicId={micId}
        selectedCameraId={cameraId}
        onMicSelect={rememberAudioInput}
        onCameraSelect={rememberCamera}
      />
      <AgeQuestion {...age.question} />
    </>
  );
}
